import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TableSkeleton from "../../Reuseable/TableSkeleton";
import ActivityLogService from "../../../Services/ActivityLogService";
import secureLocalStorage from "react-secure-storage";
import DataParser from "../../../utlits/Dataparser";
import Pagination from "../../Reuseable/Pagination";
import { paginate } from "../../../utlits/Paginate.js";
import { Helmet } from "react-helmet";
import { Tooltip, DatePicker, Button } from "antd";
import { CSVLink } from "react-csv";
import moment from "moment";

const { RangePicker } = DatePicker;

const ActivityLogs = () => {
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [dateRange, setDateRange] = useState([null, null]);
    const [exportData, setExportData] = useState([]);
    const [isExporting, setIsExporting] = useState(false);
    const csvLink = React.useRef();

    // Auth & Company Info
    const loggedUser = JSON.parse(secureLocalStorage.getItem("adminInfo"));
    const companyId = loggedUser?.user?.userdata?.company_id;

    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });

    useEffect(() => {
        if (companyId) {
            const start = dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : "";
            const end = dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : "";
            fetchLogs(currentPage, searchTerm, start, end);
        }
    }, [companyId, currentPage, pageSize, searchTerm, dateRange]);

    const fetchLogs = async (page = 1, search = "", start = "", end = "") => {
        try {
            setLoading(true);
            const res = await ActivityLogService.getLogs(companyId, page, pageSize, search, start, end);
            if (res.Good) {
                setLogs(res.data);
                setFilteredLogs(res.data);
                setPagination(res.pagination);
            }
        } catch (error) {
            console.error("Failed to fetch logs:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            setIsExporting(true);
            const start = dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : "";
            const end = dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : "";

            // Limit 0 to fetch all filtered records
            const res = await ActivityLogService.getLogs(companyId, 1, 0, searchTerm, start, end);

            if (res.Good && res.data) {
                const csvData = res.data.map(log => ({
                    "Date & Time": formatDate(log.createdAt),
                    "Actor": log.actor_name || "System",
                    "Role": log.actor_role || "N/A",
                    "Action": log.action,
                    "Module": log.module,
                    "Entity": `${log.entity_type?.replace(/_/g, ' ')} ${log.entity_name ? `(${log.entity_name})` : ''}`,
                    "IP Address": log.ip_address || "N/A"
                }));
                setExportData(csvData);

                // Trigger download
                setTimeout(() => {
                    csvLink.current.link.click();
                    setIsExporting(false);
                }, 100);
            }
        } catch (error) {
            console.error("Export failed:", error);
            setIsExporting(false);
        }
    };

    // 🟢 Component to show granular diffs (Improved with Toggle & Better Comparison)
    const LogChangeDetails = ({ oldVal, newVal }) => {
        const [isExpanded, setIsExpanded] = React.useState(false);
        if (!oldVal || !newVal) return null;

        const ignoredKeys = [
            'id', '_id', 'company_id', 'createdAt', 'updatedAt', '__v', 'password',
            'created_by', 'updated_by', 'identifier', 'company_identifier', 'module', 'parent_id'
        ];
        const diffs = [];

        const o = oldVal || {};
        const n = newVal || {};

        const allKeys = Array.from(new Set([...Object.keys(o), ...Object.keys(n)]));

        allKeys.forEach(key => {
            if (ignoredKeys.includes(key)) return;

            // 🟢 Refinement: If it's an update, and the key is NOT in newVal, 
            // it means it's a partial update and this field didn't change.
            if (!(key in n)) return;

            const val1 = o[key];
            const val2 = n[key];

            const from = formatLogValue(val1);
            const to = formatLogValue(val2);

            if (from !== to) {
                // Also ignore if they are just different types of "empty" (like "" vs null)
                const isVal1Empty = val1 === null || val1 === undefined || val1 === "";
                const isVal2Empty = val2 === null || val2 === undefined || val2 === "";
                if (isVal1Empty && isVal2Empty) return;

                diffs.push({
                    key: key.replace(/_/g, ' '),
                    from,
                    to
                });
            }
        });

        if (diffs.length === 0) return null;

        const displayDiffs = isExpanded ? diffs : diffs.slice(0, 5);
        const hasMore = diffs.length > 5;

        return (
            <div className="mt-2 p-2 rounded shadow-sm" style={{ fontSize: '11px', backgroundColor: 'rgba(13, 223, 171, 0.05)', borderLeft: '3px solid #0bc698', maxWidth: '100%' }}>
                {displayDiffs.map((d, i) => (
                    <div key={i} className="mb-1 d-flex align-items-center" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <span className="fw-bold text-capitalize me-1" style={{ color: '#008b8e', whiteSpace: 'nowrap', flexShrink: 0 }}>{d.key}:</span>
                        <Tooltip title={<div style={{ maxWidth: '350px', wordBreak: 'break-word' }}><strong>{d.key}</strong>: <span className="text-decoration-line-through text-white-50">{d.from}</span> <i className="fas fa-arrow-right mx-1"></i> {d.to}</div>}>
                            <div className="d-flex align-items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, cursor: 'help' }}>
                                <span className="text-muted text-decoration-line-through small me-1" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.from}</span>
                                <i className="fas fa-arrow-right mx-1" style={{ fontSize: '10px', color: '#0bc698', flexShrink: 0 }}></i>
                                <span className="fw-bold text-success" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.to}</span>
                            </div>
                        </Tooltip>
                    </div>
                ))}

                {hasMore && (
                    <div
                        className="text-primary mt-1 fw-bold"
                        style={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '10px' }}
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? "Show less" : `...and ${diffs.length - 5} more changes`}
                    </div>
                )}
            </div>
        );
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        setCurrentPage(1);
    };

    const handelPageChange = (e, page) => {
        e.preventDefault();
        setCurrentPage(page);
    };

    const getActionBadgeClass = (action) => {
        switch (action?.toLowerCase()) {
            case 'created': return 'badge bg-success text-white';
            case 'updated': return 'badge bg-info text-white';
            case 'deleted': return 'badge bg-danger text-white';
            case 'status_update': return 'badge bg-warning text-white';
            case 'approved': return 'badge bg-success text-white';
            case 'rejected': return 'badge bg-danger text-white';
            default: return 'badge bg-secondary text-white';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatLogValue = (value, truncate = true) => {
        if (value === null || value === undefined) return "-";

        let result = "";

        if (Array.isArray(value)) {
            if (value.length === 0) return "-";
            result = value.map(item => formatLogValue(item, false)).join(', ');
        } else if (typeof value === 'object') {
            const ignoredKeys = ['id', '_id', 'company_id', 'createdAt', 'updatedAt', '__v', 'password', 'created_by', 'updated_by'];
            const values = Object.entries(value)
                .filter(([key]) => !ignoredKeys.includes(key))
                .map(([_, val]) => formatLogValue(val, false));

            if (values.length === 0) return "-";
            result = values.join(', ');
        } else {
            result = String(value);
        }

        // 🟢 Truncate if too long (e.g. for Days array)
        if (truncate && result.length > 60) {
            return result.substring(0, 60) + "...";
        }

        return result;
    };



    return (
        <>
            <Helmet>
                <title>Activity Logs - Planit</title>
            </Helmet>
            <div className="main-content">
                <div className="page-content Pagebody" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12 px-0">
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-lg-12 px-0">
                                <div className="card" style={{ backgroundColor: "#fff", boxShadow: "none", borderRadius: 0, border: "none" }}>
                                    <div className="card-body p-0">

                                        {/* Search & Date Filter */}
                                        <div className="row mb-3 mx-0 align-items-center" style={{ paddingTop: "10px" }}>
                                            <div className="col-md-4 ps-0">
                                                <div className="float-input-wrap float-input-icon activity-log-search" style={{ margin: "0 !important", marginBottom: "0px" }}>
                                                    <input
                                                        type="text"
                                                        placeholder=" "
                                                        value={searchTerm}
                                                        className="form-control-custom-focus"
                                                        onChange={handleSearch}
                                                    />
                                                    <label style={{ fontSize: "13px", left: "14px" }}>Search by ID, User, Action, or Module...</label>
                                                    {searchTerm
                                                        ? <i className="uil-times" onMouseDown={(e) => { e.preventDefault(); setSearchTerm(""); }} style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: "16px", cursor: "pointer", zIndex: 20, pointerEvents: "auto" }} />
                                                        : <i className="uil-search" style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", color: "#D2D2D2", fontSize: "15px", pointerEvents: "none", zIndex: 20 }} />
                                                    }
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className={`float-select-wrap custom-filter ${(dateRange && dateRange[0]) ? "has-value" : ""}`} style={{ marginTop: 0 }}>
                                                    <label>Activity Period</label>
                                                    <RangePicker
                                                        value={dateRange}
                                                        onChange={handleDateRangeChange}
                                                        allowClear={false}
                                                        separator=""
                                                        placeholder={['', '']}
                                                        suffixIcon={
                                                            <div style={{
                                                                backgroundColor: "#F4F4F4",
                                                                borderRadius: "6px",
                                                                width: "24px",
                                                                height: "24px",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                marginRight: "-4px"
                                                            }}>
                                                                <i className="uil-schedule" style={{ color: "#D2D2D2", fontSize: "15px" }} />
                                                            </div>
                                                        }
                                                        style={{
                                                            width: '100%',
                                                            height: '34px !important',
                                                            borderRadius: '5px !important',
                                                            border: '1.5px solid #F2F2F2',
                                                            margin: "0 !important"
                                                        }}
                                                    />
                                                    {(dateRange && dateRange[0]) && (
                                                        <i
                                                            className="uil-times"
                                                            onClick={() => {
                                                                setDateRange([null, null]);
                                                                handleDateRangeChange([null, null]);
                                                            }}
                                                            style={{
                                                                position: "absolute", right: "30px", top: "50%",
                                                                transform: "translateY(-50%)", color: "#aaa",
                                                                fontSize: "16px", cursor: "pointer", zIndex: 2,
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-4 text-end pe-0">
                                                <Button
                                                    type="button"
                                                    className="btn no-hover-effect"
                                                    onClick={handleExport}
                                                    loading={isExporting}
                                                    style={{
                                                        backgroundColor: "#0DDFAB",
                                                        borderColor: "#0DDFAB",
                                                        height: "34px !important",
                                                        fontWeight: "600",
                                                        borderRadius: "5px",
                                                        color: "#fff",
                                                        boxShadow: "none",
                                                        margin: "0 !important"
                                                    }}
                                                >
                                                    <i className="fas fa-file-export me-2"></i> Export CSV
                                                </Button>
                                                <style>{`
                                                    .float-input-wrap label {
                                                        top: 50% !important;
                                                        transform: translateY(-50%) !important;
                                                        transition: all 0.2s ease !important;
                                                    }
                                                    .float-input-wrap input:focus ~ label,
                                                    .float-input-wrap input:not(:placeholder-shown) ~ label {
                                                        top: 0px !important;
                                                        font-size: 11px !important;
                                                        background: #fff !important;
                                                    }
                                                    .activity-log-search input {
                                                        height: 34px !important;
                                                        min-height: 34px !important;
                                                        padding: 0 38px 0 14px !important;
                                                        border-radius: 5px !important;
                                                        border: 1.5px solid #F2F2F2 !important;
                                                        font-size: 13px !important;
                                                    }
                                                    .activity-log-search input::placeholder {
                                                        color: #D2D2D2 !important;
                                                    }
                                                    .activity-log-search label {
                                                        color: #D2D2D2 !important;
                                                    }
                                                    .activity-log-search:hover input,
                                                    .activity-log-search input:focus {
                                                        border-color: #1D1D1D !important;
                                                    }
                                                    .activity-log-search:hover input::placeholder,
                                                    .activity-log-search input:focus::placeholder {
                                                        color: #1D1D1D !important;
                                                    }
                                                    .custom-filter.float-select-wrap .ant-picker {
                                                        height: 34px !important;
                                                        min-height: 34px !important;
                                                        border-radius: 5px !important;
                                                        margin: 0 !important;
                                                    }
                                                    .custom-filter.float-select-wrap .ant-picker-input > input {
                                                        line-height: 30px !important;
                                                        font-size: 13px !important;
                                                    }
                                                    .custom-filter.float-select-wrap label {
                                                        font-size: 11px !important;
                                                        font-weight: 600 !important;
                                                    }
                                                    .ant-picker-range .ant-picker-active-bar {
                                                        background: #1D1D1D !important;
                                                    }
                                                    .form-control-custom-focus:focus {
                                                        border-color: #1D1D1D !important;
                                                        box-shadow: none !important;
                                                        outline: none !important;
                                                    }
                                                    .float-input-wrap:hover input {
                                                        border-color: #1D1D1D !important;
                                                    }
                                                    .float-input-wrap:hover i,
                                                    .float-input-wrap:focus-within i,
                                                    .activity-log-search:hover i,
                                                    .activity-log-search:focus-within i {
                                                        color: #1D1D1D !important;
                                                    }
                                                    .float-input-wrap:hover label,
                                                    .float-input-wrap:focus-within label {
                                                        color: #1D1D1D !important;
                                                    }
                                                    .custom-filter.float-select-wrap .ant-picker-focused,
                                                    .custom-filter.float-select-wrap .ant-picker:hover {
                                                        border-color: #1D1D1D !important;
                                                        box-shadow: none !important;
                                                    }
                                                    .custom-filter.float-select-wrap:hover label,
                                                    .custom-filter.float-select-wrap:focus-within label {
                                                        color: #1D1D1D !important;
                                                    }
                                                    .custom-filter.float-select-wrap:hover .ant-picker-suffix div,
                                                    .custom-filter.float-select-wrap:focus-within .ant-picker-suffix div {
                                                        background-color: #1D1D1D !important;
                                                    }
                                                    .custom-filter.float-select-wrap:hover .ant-picker-suffix div i,
                                                    .custom-filter.float-select-wrap:focus-within .ant-picker-suffix div i {
                                                        color: #fff !important;
                                                    }
                                                    .custom-filter.float-select-wrap .ant-picker-focused {
                                                        border-color: #1D1D1D !important;
                                                        box-shadow: none !important;
                                                        outline: none !important;
                                                    }
                                                    .no-hover-effect:hover, .no-hover-effect:active, .no-hover-effect:focus {
                                                        background-color: #0DDFAB !important;
                                                        border-color: #0DDFAB !important;
                                                        color: #fff !important;
                                                        opacity: 0.9;
                                                    }
                                                `}</style>
                                                <CSVLink
                                                    data={exportData}
                                                    filename={`activity_logs_${moment().format('YYYYMMDD')}.csv`}
                                                    className="hidden"
                                                    ref={csvLink}
                                                    target="_blank"
                                                />
                                            </div>
                                        </div>

                                        <div className="table-responsive listing-table-wrapper">
                                            <table className="table mb-0 custom-table listing-table" style={{ width: "100%", tableLayout: "fixed" }}>
                                                <thead className="tableHeaderbg">
                                                    <tr className="tableHeaderbg">
                                                        <th style={{ width: "160px" }}>Date & Time</th>
                                                        <th style={{ width: "450px" }}>Description</th>
                                                        <th className="text-start" style={{ width: "130px" }}>User</th>
                                                        <th className="text-start" style={{ width: "110px" }}>Role</th>
                                                        <th className="text-start" style={{ width: "90px" }}>Action</th>
                                                        <th className="text-start" style={{ width: "90px" }}>Module</th>
                                                        <th className="text-start" style={{ width: "110px" }}>IP Address</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loading ? (
                                                        <TableSkeleton cols={7} rows={6} />
                                                    ) : (
                                                        <>
                                                            {filteredLogs && filteredLogs.length > 0 ? (
                                                                filteredLogs.map((log) => (
                                                                    <tr key={log.id}>
                                                                        <td style={{ fontSize: "12px" }}>{formatDate(log.createdAt)}</td>
                                                                        <td>
                                                                            <Tooltip title={`${log.actor_name || "System"} ${log.action} ${log.entity_type?.replace(/_/g, ' ')} ${log.entity_name ? `(${log.entity_name})` : ''}`}>
                                                                                <div className="fw-bold mb-1 text-capitalize" style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", fontSize: "13px" }}>
                                                                                    {log.actor_name || "System"} {log.action} {log.entity_type?.replace(/_/g, ' ')} {log.entity_name ? `(${log.entity_name})` : ''}
                                                                                </div>
                                                                            </Tooltip>
                                                                            {log.action === "updated" && (
                                                                                <LogChangeDetails oldVal={log.old_values} newVal={log.new_values} />
                                                                            )}
                                                                        </td>
                                                                        <td className="text-start" style={{ fontSize: "12px" }}>
                                                                            {log.actor_name || "System"}
                                                                        </td>
                                                                        <td className="text-start">
                                                                            <span className="badge bg-light text-dark border" style={{ fontSize: "10px" }}>
                                                                                {log.actor_role || "N/A"}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-start">
                                                                            <span className={getActionBadgeClass(log.action)} style={{ fontSize: "10px" }}>
                                                                                {log.action}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-capitalize text-start" style={{ fontSize: "12px" }}>{log.module}</td>
                                                                        <td className="text-start" style={{ fontSize: "12px", color: "#666" }}>
                                                                            {log.ip_address || "N/A"}
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="7" className="text-center py-4">
                                                                        No activity logs found.
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </>
                                                    )}
                                                </tbody>
                                            </table>

                                            {pagination.total > pageSize && (
                                                <div className="d-flex justify-content-center mt-4">
                                                    <Pagination
                                                        itemCount={pagination.total}
                                                        currentPage={currentPage}
                                                        pageSize={pageSize}
                                                        onPageChange={handelPageChange}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ActivityLogs;
